import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import { useQuery } from '@tanstack/react-query';
import { useIntegrationService } from '../../services/integration.service';
import { useIntegrationsStatusContext } from '../../context/IntegrationsStatusContext';
import { Platform } from '../../services/integration.types';
import PlatformSelectionGrid from './PlatformSelectionGrid';
import Breadcrumb from '../common/Breadcrumb';
import PageLayout from '../common/PageLayout';

const IntegrationSelectPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { integrations } = useIntegrationsStatusContext();
    const [selectedPlatformName, setSelectedPlatformName] = useState<string | null>(null);
    const [selectedImportType, setSelectedImportType] = useState<string | null>(null);
    const integrationService = useIntegrationService();

    // Fetch platforms to ensure data is loaded
    const { data: platforms = [], isLoading } = useQuery({
        queryKey: ['platforms'],
        queryFn: async () => {
            const allPlatforms = await integrationService.getAllPlatforms();
            return allPlatforms.filter((platform) => !platform.legacy);
        },
        staleTime: 5 * 60 * 1000,
    });

    const handlePlatformSelect = (platform: Platform) => {
        setSelectedPlatformName(platform.name);
        setSelectedImportType('api'); // Default to API import type
        // Navigate to the create page with the selected platform
        navigate(`/integrations/add/${platform.name}?type=api`);
    };

    const handleFileUploadSelect = () => {
        // Navigate to file upload integration
        navigate(`/integrations/add/excelUpload?type=csv`);
    };

    // Check if a platform already has an integration
    const isIntegrationAdded = (platform: Platform): boolean => {
        if (!integrations || platform.platform_type !== 'exchange') return false;
        return integrations.some((integration) => integration.platformName === platform.name && integration.jobId);
    };

    const breadcrumbItems = [
        { label: t('integrations.title'), path: '/integrations' },
        { label: t('integrations.selectPlatform') }
    ];

    if (isLoading) {
        return (
            <PageLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <PlatformSelectionGrid
                    platforms={platforms}
                    isLoading={isLoading}
                    onPlatformSelect={handlePlatformSelect}
                    onFileUploadSelect={handleFileUploadSelect}
                    checkIfIntegrated={isIntegrationAdded}
                    maxHeight="calc(100vh - 280px)"
                />
            </Box>
        </PageLayout>
    );
};

export default IntegrationSelectPage;